// components/ClientSchedulesDrawer/ScheduleFilter.jsx
import React from "react";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Stack,
  alpha,
  useTheme,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  RestartAlt as ResetIcon,
  Today as TodayIcon,
  ArrowBack as YesterdayIcon,
  CalendarToday as TomorrowIcon,
  DateRange as WeeklyIcon,
  Filter1 as Week1Icon,
  Filter2 as Week2Icon,
} from "@mui/icons-material";
import CustomInput from "../../FormFields/CustomInput";

const ScheduleFilter = ({
  search,
  setSearch,
  filter,
  setFilter,
  status,
  setStatus,
  onReset,
  statusOptions,
}) => {
  const theme = useTheme();

  return (
    <Box sx={{ mb: 2 }}>
      <Stack spacing={1.5}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5,
            alignItems: "center",
          }}
        >
          <Box sx={{ flex: "1 1 200px", minWidth: "150px" }}>
            <CustomInput
              placeholder="Search schedules..."
              placeholderFontSize="0.75rem"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  backgroundColor: "#FAFBFC",
                  "& fieldset": {
                    borderColor: "#E2E8F0",
                  },
                  "&:hover fieldset": {
                    borderColor: theme.palette.primary.main,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: theme.palette.primary.main,
                  },
                },
                "& .MuiInputBase-input": {
                  fontSize: "0.85rem",
                  padding: "8px 14px",
                },
              }}
              InputProps={{
                startAdornment: (
                  <SearchIcon
                    sx={{ fontSize: 20, color: "#94A3B8", mr: 0.5 }}
                  />
                ),
                endAdornment: search && (
                  <IconButton
                    size="small"
                    onClick={() => setSearch("")}
                    sx={{ padding: "2px" }}
                  >
                    <ClearIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                ),
              }}
            />
          </Box>

          <Box sx={{ flex: "1 1 130px", minWidth: "120px" }}>
            <FormControl
              size="small"
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  backgroundColor: "#FAFBFC",
                  "& fieldset": {
                    borderColor: "#E2E8F0",
                  },
                  "&:hover fieldset": {
                    borderColor: theme.palette.primary.main,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: theme.palette.primary.main,
                  },
                },
                "& .MuiSelect-select": {
                  fontSize: "0.85rem",
                  padding: "8px 14px",
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.75rem",
                },
              }}
            >
              <InputLabel>Status</InputLabel>
              <Select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                label="Status"
              >
                {statusOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Tooltip title="Reset Filters">
            <IconButton
              onClick={onReset}
              sx={{
                color: "#64748b",
                bgcolor: "#FAFBFC",
                borderRadius: "8px",
                border: "1px solid #E2E8F0",
                "&:hover": {
                  bgcolor: "#F1F5F9",
                },
              }}
            >
              <ResetIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <Box>
          <ToggleButtonGroup
            value={filter}
            exclusive
            onChange={(e, newValue) => {
              if (newValue !== null) setFilter(newValue);
            }}
            aria-label="time filter"
            sx={{
              display: "flex",
              gap: 0.5,
              width: "100%",
              "& .MuiToggleButton-root": {
                flex: 1,
                py: 1,
                px: 1,
                borderRadius: "8px !important",
                border: "1px solid #E2E8F0",
                backgroundColor: "#FAFBFC",
                fontSize: "0.7rem",
                fontWeight: 600,
                color: "#667085",
                textTransform: "none",
                transition: theme.transitions.create(
                  ["background-color", "border-color", "color", "transform"],
                  { duration: 200 }
                ),
                "&.Mui-selected": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                  transform: "scale(1.02)",
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.14),
                  },
                },
                "&:hover": {
                  backgroundColor: "#F1F4F9",
                },
              },
            }}
          >
            <ToggleButton value="yesterday" aria-label="yesterday">
              <YesterdayIcon sx={{ fontSize: 14, mr: 0.5 }} />
              Yesterday
            </ToggleButton>
            <ToggleButton value="today" aria-label="today">
              <TodayIcon sx={{ fontSize: 14, mr: 0.5 }} />
              Today
            </ToggleButton>
            <ToggleButton value="tomorrow" aria-label="tomorrow">
              <TomorrowIcon sx={{ fontSize: 14, mr: 0.5 }} />
              Tomorrow
            </ToggleButton>
            <ToggleButton value="week1" aria-label="week1">
              <Week1Icon sx={{ fontSize: 14, mr: 0.5 }} />
              Week 1
            </ToggleButton>
            <ToggleButton value="week2" aria-label="week2">
              <Week2Icon sx={{ fontSize: 14, mr: 0.5 }} />
              Week 2
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Stack>
    </Box>
  );
};

export default ScheduleFilter;